import { SFDC_ENTITIES, Project } from "@amzn/afxtaco-types";
import {
  CreateProjectRequest,
  UpdateProjectRequest,
} from "../../api/projects/projects";

interface PersistArgs {
  accountId: string;
  opportunityId: string;
  /**
   * Whether the project row already exists — true when it was eager-created for
   * an attachment upload before send.
   */
  alreadyExists: boolean;
  /**
   * `useProjects().createProject` / `.updateProject`, not the raw API functions.
   *
   * This matters: the hook versions dispatch the returned record into Redux, and
   * Redux is what the Overview's Customer box reads. Calling the API directly
   * persisted the IDs but left the store untouched, so the box stayed empty until
   * something else happened to refetch the project — which on a chat-only first
   * turn is not until the turn completes with a sealed version. That was the
   * "takes a while to populate" delay.
   */
  createProject: (
    projectId: string,
    data: CreateProjectRequest,
  ) => Promise<Project>;
  updateProject: (
    projectId: string,
    data: UpdateProjectRequest,
  ) => Promise<Project | null>;
}

/**
 * Writes the Salesforce IDs from the start-project form onto the project record,
 * so they become the project's authoritative customer context rather than
 * surviving only as text in the first prompt.
 *
 * Two paths because the project may or may not exist yet at send time: the
 * backend normally creates it lazily during the turn, but the attachment flow
 * eager-creates it first.
 *
 * Callers should treat failure as non-fatal — the IDs still reached the agent on
 * the turn's payload, so a failed write costs the Overview's Customer box, not
 * the grounding itself.
 *
 * @param projectId - the project being started
 * @param args - the trimmed IDs, whether the row exists, and the store-aware
 *   persistence functions from `useProjects()`
 */
/**
 * Builds the optimistic project record to seed the store with before navigating.
 *
 * The project view does not fetch during the new-project flow — there is no row
 * to fetch yet, the backend creates it during the turn — so without this the
 * Overview's Customer box has nothing to read and sits empty until the turn
 * completes. That was the visible gap: IDs typed on the start page took until the
 * agent finished its first message to appear.
 *
 * Deliberately minimal. Only the IDs are asserted, because they are the one thing
 * the user just told us; the name and description are the agent's to write, and
 * the real record replaces this wholesale as soon as the create call returns.
 */
export function optimisticCustomerProject(
  projectId: string,
  {
    accountId,
    opportunityId,
  }: Pick<PersistArgs, "accountId" | "opportunityId">,
  now: string,
): Project {
  const associatedEntities: Record<string, string[]> = {};
  if (accountId) {
    associatedEntities[SFDC_ENTITIES.ACCOUNT.key] = [accountId];
  }
  if (opportunityId) {
    associatedEntities[SFDC_ENTITIES.OPPORTUNITY.key] = [opportunityId];
  }

  return {
    projectId,
    // The agent names the project during the turn; until then the projects list
    // falls back to "Untitled Project", exactly as it does for any unnamed row.
    projectName: "",
    latestVersion: 0,
    createdAt: now,
    updatedAt: now,
    associatedEntities,
  };
}

export async function persistCustomerContext(
  projectId: string,
  {
    accountId,
    opportunityId,
    alreadyExists,
    createProject,
    updateProject,
  }: PersistArgs,
): Promise<void> {
  const ids = {
    ...(accountId ? { accountId } : {}),
    ...(opportunityId ? { opportunityId } : {}),
  };

  if (alreadyExists) {
    await updateProject(projectId, ids);
    return;
  }

  await createProject(projectId, ids);
}
